import { FormEvent, useMemo, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, UserRole } from '@services/auth';
import { listCompanies, type Company } from '@services/companies';
import { getGardenerInviteByCode } from '@services/gardenerInvites';
import { acceptGardenerInvite } from '@services/gardenerInvites';
import { consumeGuestPendingForGeneration } from '@utils/guestPlanResume';
import {
	isValidEmail,
	isValidRegistrationPassword,
	normalizeEmail,
} from '@utils/authValidation';
import { useI18n } from '../../i18n';
import { trackActivityEvent, usePageActivityTracker } from '@services/activityTracker';

type LocationState = {
	from?: Location;
};

// Declare Google types for TypeScript
declare global {
	interface Window {
		google?: {
			accounts: {
				id: {
					initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void;
					renderButton: (element: HTMLElement, options: { theme?: string; size?: string; text?: string; width?: number }) => void;
					prompt: () => void;
				};
			};
		};
	}
}

export default function LoginPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const logoSrc = `${import.meta.env.BASE_URL}logo-transparent.png`;
	const [searchParams] = useSearchParams();
	const { login, loginWithGoogle, register, refreshUser, isLoading, error: authError } = useAuth();
	const { language } = useI18n();
	usePageActivityTracker('login_page');
	const googleButtonRef = useRef<HTMLDivElement>(null);
	const googleButtonWrapperRef = useRef<HTMLDivElement>(null);
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const roleOptions: { value: UserRole; label: string; description: string }[] = useMemo(
		() => [
			{
				value: 'seeker',
				label: copy('Garden Seeker', 'Gartensuchende', 'Vlasnik vrta'),
				description: copy(
					'Create garden plans and track project progress.',
					'Erstellen Sie Gartenpläne und verfolgen Sie den Projektfortschritt.',
					'Kreirajte vrtne planove i pratite napredak projekta.'
				)
			},
			{
				value: 'gardener',
				label: copy('Gardener', 'Gärtner', 'Vrtlar'),
				description: copy(
					'Browse the marketplace and send offers — independently or as part of a team.',
					'Durchsuchen Sie den Marktplatz und senden Sie Angebote — selbstständig oder im Team.',
					'Pregledajte tržnicu i šaljite ponude — samostalno ili u timu.'
				)
			},
			{
				value: 'company',
				label: copy('Company', 'Unternehmen', 'Tvrtka'),
				description: copy(
					'Save time on visits, planning, and communication — for your team and your clients.',
					'Sparen Sie Zeit bei Terminen, Planung und Kommunikation — für Team und Kunden.',
					'Uštedite vrijeme na posjetima, planiranju i komunikaciji — za tim i klijente.'
				)
			}
		],
		[language]
	);

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [displayName, setDisplayName] = useState('');
	const [role, setRole] = useState<UserRole>('seeker');
	const [roleTouched, setRoleTouched] = useState(false);
	const [companyName, setCompanyName] = useState('');
	const [requestToJoinCompanyId, setRequestToJoinCompanyId] = useState<number | ''>('');
	const [companiesList, setCompaniesList] = useState<Company[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isRegisterMode, setIsRegisterMode] = useState(false);

	// Capture invite code passed to /login?invite=CODE so we can send
	// seekers straight into My Plans with the code still in the URL after login.
	const inviteCodeFromUrl = useMemo(() => {
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('invite') || params.get('code');
			return raw && raw.trim() ? raw.trim() : null;
		} catch {
			return null;
		}
	}, [location.search]);

	// Company -> gardener invite link, used for gardeners that don't yet have an account
	const gardenerInviteCodeFromUrl = useMemo(() => {
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('gardenerInvite');
			return raw && raw.trim() ? raw.trim() : null;
		} catch {
			return null;
		}
	}, [location.search]);

	const guestPlanFromUrl = searchParams.get('guestPlan') === '1';
	const seekerEntryFromUrl = searchParams.get('entry') === 'seeker';
	const seekerEntryFromSession = (() => {
		try {
			return sessionStorage.getItem('publicEntry') === 'seeker';
		} catch {
			return false;
		}
	})();
	const seekerOnlyRegistration = seekerEntryFromUrl || seekerEntryFromSession;

	const [gardenerInviteInfo, setGardenerInviteInfo] = useState<{ code: string; companyName: string } | null>(null);
	const [isCheckingGardenerInvite, setIsCheckingGardenerInvite] = useState(false);

	useEffect(() => {
		let cancelled = false;
		if (!gardenerInviteCodeFromUrl) {
			setGardenerInviteInfo(null);
			setIsCheckingGardenerInvite(false);
			return;
		}
		(async () => {
			try {
				setIsCheckingGardenerInvite(true);
				const details = await getGardenerInviteByCode(gardenerInviteCodeFromUrl);
				if (cancelled) return;
				setGardenerInviteInfo({ code: details.invite.code, companyName: details.company.name });
			} catch {
				if (!cancelled) setGardenerInviteInfo(null);
			} finally {
				if (!cancelled) setIsCheckingGardenerInvite(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [gardenerInviteCodeFromUrl]);

	// If arriving via a gardener invite link, default registration role to gardener
	// (but allow the user to override manually).
	useEffect(() => {
		if (!gardenerInviteCodeFromUrl) return;
		if (!isRegisterMode) return;
		if (roleTouched) return;
		if (role !== 'gardener') {
			setRole('gardener');
			setCompanyName('');
			setRequestToJoinCompanyId('');
		}
	}, [gardenerInviteCodeFromUrl, isRegisterMode, roleTouched, role]);

	useEffect(() => {
		if (!guestPlanFromUrl) return;
		const mode = searchParams.get('mode');
		setIsRegisterMode(mode !== 'login');
		setRole('seeker');
		setRoleTouched(false);
	}, [guestPlanFromUrl, searchParams]);

	useEffect(() => {
		if (!seekerOnlyRegistration || !isRegisterMode) return;
		if (role !== 'seeker') setRole('seeker');
		if (companyName) setCompanyName('');
		if (requestToJoinCompanyId) setRequestToJoinCompanyId('');
	}, [seekerOnlyRegistration, isRegisterMode, role, companyName, requestToJoinCompanyId]);

	const from = useMemo(() => {
		const state = location.state as LocationState | undefined;
		if (state?.from) {
			// Preserve full target including query string and hash so things like ?invite=CODE survive login
			const fromLoc = state.from as any;
			const pathname: string = fromLoc.pathname || '/app';
			const search: string = fromLoc.search || '';
			const hash: string = fromLoc.hash || '';
			return `${pathname}${search}${hash}`;
		}
		// If there is no explicit "from" page, but we have an invite code,
		// send seekers directly to My Plans with that invite applied.
		if (inviteCodeFromUrl) {
			return `/app/seeker/plans?invite=${encodeURIComponent(inviteCodeFromUrl)}`;
		}
		return '/app';
	}, [location.state, inviteCodeFromUrl]);

	// Initialize Google Sign-In
	useEffect(() => {
		if (isRegisterMode) return; // Only show Google sign-in on login, not registration

		const loadGoogleScript = () => {
			// Check if script already loaded
			if (window.google?.accounts?.id) {
				initializeGoogleSignIn();
				return;
			}

			// Load Google Identity Services script
			const script = document.createElement('script');
			script.src = 'https://accounts.google.com/gsi/client';
			script.async = true;
			script.defer = true;
			script.onload = () => {
				// Wait a bit for Google to be fully ready
				setTimeout(initializeGoogleSignIn, 100);
			};
			document.head.appendChild(script);
		};

		const initializeGoogleSignIn = () => {
			if (!window.google?.accounts?.id || !googleButtonRef.current) return;

			// Get Google Client ID from environment or use a default
			// In production, you should set this in your .env file
			const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

			if (!clientId) {
				console.warn('Google Client ID not configured. Google Sign-In will not work.');
				return;
			}

			window.google.accounts.id.initialize({
				client_id: clientId,
				callback: handleGoogleSignIn,
			});

			// Render the button (Google accepts width in pixels, max 400)
			if (googleButtonRef.current) {
				const wrapperWidth = googleButtonWrapperRef.current?.clientWidth ?? 0;
				const buttonWidth = Math.min(wrapperWidth > 0 ? wrapperWidth : 320, 400);
				window.google.accounts.id.renderButton(googleButtonRef.current, {
					theme: 'outline',
					size: 'large',
					text: 'signin_with',
					width: buttonWidth,
				});
			}
		};

		const handleGoogleSignIn = async (response: { credential: string }) => {
			try {
				await loginWithGoogle(response.credential);
				sessionStorage.setItem('justLoggedIn', 'true');
				let storedUser: { role?: UserRole } | null = null;
				try {
					storedUser = JSON.parse(sessionStorage.getItem('gardenhub:auth') || 'null');
				} catch {
					storedUser = null;
				}
				const resume = consumeGuestPendingForGeneration(storedUser?.role);
				if (resume === 'wrong_role') {
					setError(
						'Garden questionnaire trials continue only for garden seeker accounts. Use a seeker account or start again from the homepage.'
					);
					navigate('/app/seeker/plans', { replace: true });
					return;
				}
				if (resume) {
					navigate(resume.path, { replace: true, state: resume.state });
					return;
				}
				navigate(from, { replace: true, state: { justLoggedIn: true } });
			} catch (err: any) {
				setError(err.message || 'Google sign-in failed. Please try again.');
			}
		};

		loadGoogleScript();
	}, [isRegisterMode, loginWithGoogle, navigate, from]);

	// Load companies for optional gardener company link during registration
	useEffect(() => {
		if (!isRegisterMode || role !== 'gardener' || gardenerInviteCodeFromUrl) return;
		listCompanies().then(setCompaniesList).catch(() => setCompaniesList([]));
	}, [isRegisterMode, role, gardenerInviteCodeFromUrl]);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (isSubmitting || isLoading) {
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const normalizedEmail = normalizeEmail(email);

			if (isRegisterMode) {
				void trackActivityEvent({ event_name: 'signup_click', properties: { entry: 'login_page' } });
				// Registration
				if (!displayName.trim()) {
					setError('Name is required for registration');
					setIsSubmitting(false);
					return;
				}
				if (!isValidEmail(normalizedEmail)) {
					setError(copy('Enter a valid email address', 'Geben Sie eine gültige E-Mail-Adresse ein', 'Unesite valjanu e-mail adresu'));
					setIsSubmitting(false);
					return;
				}
				if (!isValidRegistrationPassword(password)) {
					setError(
						copy(
							'Password must be at least 8 characters and include a letter and a number or symbol',
							'Das Passwort muss mindestens 8 Zeichen lang sein und einen Buchstaben sowie eine Zahl oder ein Sonderzeichen enthalten',
							'Lozinka mora imati najmanje 8 znakova te sadržavati slovo i broj ili simbol'
						)
					);
					setIsSubmitting(false);
					return;
				}
				const lockedToGardenerInvite = !!gardenerInviteCodeFromUrl;
				const lockedToSeekerEntry = seekerOnlyRegistration;
				await register({
					email: normalizedEmail,
					password,
					name: displayName.trim(),
					role: lockedToGardenerInvite ? 'gardener' : lockedToSeekerEntry ? 'seeker' : role,
					companyName:
						lockedToGardenerInvite
							? undefined
							: role === 'company' && companyName
								? companyName.trim()
								: undefined,
					requestToJoinCompanyId:
						lockedToGardenerInvite
							? undefined
							: role === 'gardener' && requestToJoinCompanyId
								? requestToJoinCompanyId
								: undefined,
					gardenerInviteCode: lockedToGardenerInvite ? gardenerInviteCodeFromUrl ?? undefined : undefined,
				});
				void trackActivityEvent({ event_name: 'signup_success' });
			} else {
				// Login
				void trackActivityEvent({ event_name: 'login_click', properties: { entry: 'login_page' } });
				if (!isValidEmail(normalizedEmail)) {
					setError(copy('Enter a valid email address', 'Geben Sie eine gültige E-Mail-Adresse ein', 'Unesite valjanu e-mail adresu'));
					setIsSubmitting(false);
					return;
				}
				await login({
					email: normalizedEmail,
					password,
				});
				void trackActivityEvent({ event_name: 'login_success' });

				// If logging in via a gardenerInvite link, auto-accept it for existing accounts.
				if (gardenerInviteCodeFromUrl) {
					try {
						const stored = sessionStorage.getItem('gardenhub:auth');
						const storedUser = stored ? (JSON.parse(stored) as { id?: string; role?: string } | null) : null;
						const userIdNum = storedUser?.id ? parseInt(String(storedUser.id), 10) : NaN;
						if (!Number.isNaN(userIdNum) && storedUser?.role === 'gardener') {
							await acceptGardenerInvite(userIdNum, gardenerInviteCodeFromUrl);
							// Refresh so UI (companyName/companyId) updates immediately after redirect.
							await refreshUser(String(userIdNum));
						}
					} catch {
						// Ignore silently; user can still proceed.
					}
				}
			}

			sessionStorage.setItem('justLoggedIn', 'true');

			let storedUser: { role?: UserRole } | null = null;
			try {
				storedUser = JSON.parse(sessionStorage.getItem('gardenhub:auth') || 'null');
			} catch {
				storedUser = null;
			}
			const resume = consumeGuestPendingForGeneration(storedUser?.role);
			if (resume === 'wrong_role') {
				setError(
					'Garden questionnaire trials continue only for garden seeker accounts. Use a seeker account or start again from the homepage.'
				);
				setIsSubmitting(false);
				navigate('/app/seeker/plans', { replace: true });
				return;
			}
			if (resume) {
				navigate(resume.path, { replace: true, state: resume.state });
				setIsSubmitting(false);
				return;
			}

			navigate(from, { replace: true, state: { justLoggedIn: true } });
		} catch (err: any) {
			void trackActivityEvent({ event_name: isRegisterMode ? 'signup_failure' : 'login_failure' });
			setError(err.message || 'Authentication failed. Please try again.');
			setIsSubmitting(false);
		}
	};

	const displayError = error || authError;

	type SidebarFeature = { icon: string; title: string; description: string };
	type SidebarContent = {
		eyebrow: string;
		title: string;
		intro: string;
		features: SidebarFeature[];
		calloutTitle: string;
		calloutBody: string;
	};

	const sidebarContent = useMemo((): SidebarContent => {
		if (!isRegisterMode) {
			return {
				eyebrow: copy('Gardens, made simpler', 'Gärten, einfacher gemacht', 'Vrtovi, jednostavnije'),
				title: copy(
					'From first idea to finished garden',
					'Von der ersten Idee bis zum fertigen Garten',
					'Od prve ideje do gotovog vrta'
				),
				intro: copy(
					'HortEra brings homeowners, gardeners, and landscaping companies together — so every garden project moves forward with less guesswork and more clarity.',
					'HortEra verbindet Hausbesitzer, Gärtner und Gartenbauunternehmen — damit jedes Gartenprojekt mit weniger Unsicherheit und mehr Klarheit vorankommt.',
					'HortEra povezuje vlasnike kuća, vrtlare i vrtne tvrtke — kako bi svaki vrtni projekt napredovao s manje nagađanja i više jasnoće.'
				),
				features: [
					{
						icon: 'bi-flower1',
						title: copy('Visual garden planning', 'Visuelle Gartenplanung', 'Vizualno planiranje vrta'),
						description: copy(
							'Turn photos and ideas into clear, structured garden plans you can actually act on.',
							'Verwandeln Sie Fotos und Ideen in klare, strukturierte Gartenpläne, mit denen Sie wirklich arbeiten können.',
							'Pretvorite fotografije i ideje u jasne, strukturirane vrtne planove na kojima možete raditi.'
						)
					},
					{
						icon: 'bi-people-fill',
						title: copy('One place for everyone', 'Ein Ort für alle', 'Jedno mjesto za sve'),
						description: copy(
							'Seekers, gardeners, and companies each get tools built for how they actually work.',
							'Gartensuchende, Gärtner und Unternehmen erhalten jeweils Tools, die zu ihrem Arbeitsalltag passen.',
							'Vlasnici vrta, vrtlari i tvrtke dobivaju alate prilagođene načinu na koji stvarno rade.'
						)
					},
					{
						icon: 'bi-kanban-fill',
						title: copy('Progress you can see', 'Fortschritt, den Sie sehen', 'Napredak koji vidite'),
						description: copy(
							'Offers, projects, and updates live in one workspace instead of scattered messages.',
							'Angebote, Projekte und Updates an einem Ort — statt verstreut in Nachrichten.',
							'Ponude, projekti i ažuriranja na jednom mjestu — umjesto razbacanih poruka.'
						)
					}
				],
				calloutTitle: copy('Welcome back', 'Willkommen zurück', 'Dobrodošli natrag'),
				calloutBody: copy(
					'Sign in to pick up right where you left off.',
					'Melden Sie sich an und machen Sie genau dort weiter, wo Sie aufgehört haben.',
					'Prijavite se i nastavite točno tamo gdje ste stali.'
				)
			};
		}

		if (role === 'gardener') {
			return {
				eyebrow: copy('For gardeners', 'Für Gärtner', 'Za vrtlare'),
				title: copy(
					'Less admin, more time in the garden',
					'Weniger Verwaltung, mehr Zeit im Garten',
					'Manje administracije, više vremena u vrtu'
				),
				intro: copy(
					'Browse real client garden plans, send offers, and keep every project organized — whether you work solo or as part of a team.',
					'Durchsuchen Sie echte Kundengartenpläne, senden Sie Angebote und behalten Sie jedes Projekt im Blick — ob solo oder im Team.',
					'Pregledavajte stvarne vrtne planove klijenata, šaljite ponude i držite svaki projekt pod kontrolom — bilo solo ili u timu.'
				),
				features: [
					{
						icon: 'bi-search-heart',
						title: copy('Projects that fit you', 'Projekte, die zu Ihnen passen', 'Projekti koji vam odgovaraju'),
						description: copy(
							'Discover garden plans from homeowners who are ready to hire and start work.',
							'Entdecken Sie Gartenpläne von Hausbesitzern, die bereit sind zu beauftragen.',
							'Otkrijte vrtne planove vlasnika koji su spremni angažirati vrtlara.'
						)
					},
					{
						icon: 'bi-file-earmark-text-fill',
						title: copy('Offers made simple', 'Angebote leicht gemacht', 'Ponude bez komplikacija'),
						description: copy(
							'Submit offers and stay in touch with clients without juggling spreadsheets or long email chains.',
							'Senden Sie Angebote und bleiben Sie mit Kunden in Kontakt — ohne Tabellen oder lange E-Mail-Ketten.',
							'Šaljite ponude i ostajte u kontaktu s klijentima bez tablica i dugih lanaca e-pošte.'
						)
					},
					{
						icon: 'bi-calendar-check-fill',
						title: copy('Schedule, tasks, and jobs', 'Termine, Aufgaben und Jobs', 'Raspored, zadaci i poslovi'),
						description: copy(
							'Plan site visits, manage tasks, and track active jobs from one workspace.',
							'Planen Sie Besuche, verwalten Sie Aufgaben und verfolgen Sie laufende Jobs an einem Ort.',
							'Planirajte posjete, upravljajte zadacima i pratite aktivne poslove s jednog mjesta.'
						)
					}
				],
				calloutTitle: copy('Work solo or with a team?', 'Solo oder im Team?', 'Sami ili u timu?'),
				calloutBody: copy(
					'Link to a company now or skip and do it later in Settings. Running your own business? Choose Company instead.',
					'Jetzt mit einem Unternehmen verknüpfen oder später in den Einstellungen. Eigenes Unternehmen? Wählen Sie Unternehmen.',
					'Povežite se s tvrtkom sada ili kasnije u Postavkama. Vodite vlastitu tvrtku? Odaberite Tvrtka.'
				)
			};
		}

		if (role === 'company') {
			return {
				eyebrow: copy('For landscaping companies', 'Für Gartenbauunternehmen', 'Za vrtne tvrtke'),
				title: copy(
					'Save hours on every project',
					'Sparen Sie Stunden bei jedem Projekt',
					'Uštedite sate na svakom projektu'
				),
				intro: copy(
					'HortEra cuts the back-and-forth that slows landscaping teams down — fewer repeat site visits, faster plan prep, and one place to talk with clients and your crew.',
					'HortEra reduziert den Hin und Her, der Teams ausbremst — weniger erneute Vor-Ort-Termine, schnellere Planung und ein Ort für Kunden und Team.',
					'HortEra smanjuje gubljenje vremena — manje ponovnih posjeta, brža priprema planova i jedno mjesto za klijente i tim.'
				),
				features: [
					{
						icon: 'bi-geo-alt-fill',
						title: copy('Fewer wasted site visits', 'Weniger unnötige Vor-Ort-Termine', 'Manje bespotrebnih posjeta'),
						description: copy(
							'Clients share photos, measurements, and preferences upfront — so your team shows up knowing what the job needs.',
							'Kunden teilen Fotos, Maße und Wünsche im Voraus — Ihr Team weiß vor dem Termin, was zu tun ist.',
							'Klijenti unaprijed dijele fotografije, mjere i želje — tim dolazi znajući što projekt zahtijeva.'
						)
					},
					{
						icon: 'bi-file-earmark-richtext-fill',
						title: copy('Plans and offers, faster', 'Pläne und Angebote, schneller', 'Planovi i ponude, brže'),
						description: copy(
							'Build offers from structured project plans instead of rebuilding scope from scattered notes and messages.',
							'Erstellen Sie Angebote aus strukturierten Projektplänen — statt den Umfang aus Notizen und Nachrichten neu zusammenzusetzen.',
							'Gradite ponude iz strukturiranih projektnih planova umjesto da ponovo sastavljate opseg iz razbacanih bilješki.'
						)
					},
					{
						icon: 'bi-chat-dots-fill',
						title: copy('One place for every conversation', 'Ein Ort für alle Gespräche', 'Jedno mjesto za sve razgovore'),
						description: copy(
							'Keep client conversations, team updates, schedules, and tasks in one workspace — not across calls, texts, and inboxes.',
							'Kundengespräche, Team-Updates, Termine und Aufgaben an einem Ort — nicht über Anrufe, Chats und Postfächer verstreut.',
							'Razgovori s klijentima, ažuriranja tima, raspored i zadaci na jednom mjestu — ne po pozivima, porukama i inboxima.'
						)
					}
				],
				calloutTitle: copy('More time on site, less on admin', 'Mehr Zeit vor Ort, weniger Verwaltung', 'Više vremena na terenu, manje administracije'),
				calloutBody: copy(
					'Spend less time on admin and more time delivering gardens your clients love.',
					'Weniger Zeit für Verwaltung, mehr Zeit für die Umsetzung vor Ort.',
					'Manje vremena na administraciju, više na izvedbu vrtova koje klijenti vole.'
				)
			};
		}

		return {
			eyebrow: copy('For garden seekers', 'Für Gartensuchende', 'Za vlasnike vrta'),
			title: copy(
				'Turn your outdoor space into a clear plan',
				'Verwandeln Sie Ihren Außenbereich in einen klaren Plan',
				'Pretvorite vanjski prostor u jasan plan'
			),
			intro: copy(
				'Share what you envision, shape a clear garden plan, and connect with gardeners who can bring it to life — without losing track of the details.',
				'Teilen Sie Ihre Vision, erstellen Sie einen klaren Gartenplan und finden Sie Gärtner, die ihn umsetzen — ohne den Überblick zu verlieren.',
				'Podijelite svoju viziju, oblikujte jasan vrtni plan i povežite se s vrtlarima koji ga mogu ostvariti — bez gubitka detalja.'
			),
			features: [
				{
					icon: 'bi-camera-fill',
					title: copy('Clear garden plans', 'Klare Gartenpläne', 'Jasni vrtni planovi'),
					description: copy(
						'Upload photos, answer a few questions, and get a structured plan ready to share.',
						'Laden Sie Fotos hoch, beantworten Sie Fragen und erhalten Sie einen strukturierten Plan zum Teilen.',
						'Učitajte fotografije, odgovorite na pitanja i dobijte strukturirani plan za dijeljenje.'
					)
				},
				{
					icon: 'bi-envelope-heart-fill',
					title: copy('Compare offers', 'Angebote vergleichen', 'Usporedite ponude'),
					description: copy(
						'Review proposals from gardeners and companies side by side, in one place.',
						'Vergleichen Sie Angebote von Gärtnern und Unternehmen an einem Ort.',
						'Usporedite ponude vrtlara i tvrtki na jednom mjestu.'
					)
				},
				{
					icon: 'bi-graph-up-arrow',
					title: copy('Track every milestone', 'Alle Meilensteine verfolgen', 'Pratite svaku fazu'),
					description: copy(
						'Follow your project from first sketch to finished garden with updates along the way.',
						'Verfolgen Sie Ihr Projekt vom ersten Entwurf bis zum fertigen Garten.',
						'Pratite projekt od prvog skice do gotovog vrta uz ažuriranja tijekom puta.'
					)
				}
			],
			calloutTitle: copy('You are in the right place', 'Sie sind richtig hier', 'Na pravom ste mjestu'),
			calloutBody: copy(
				'For homeowners and anyone planning a garden transformation.',
				'Für Hausbesitzer und alle, die ihren Garten neu gestalten möchten.',
				'Za vlasnike kuća i sve koji planiraju transformaciju vrta.'
			)
		};
	}, [isRegisterMode, role, language]);

	const sidebarKey = isRegisterMode ? role : 'signin';

	return (
		<div className="login-page d-flex align-items-center flex-grow-1 py-5">
			<div className="container py-lg-5">
				<div className="row justify-content-center g-5">
					<div className="col-12 col-lg-5">
						<div className="auth-card card border-0 shadow-lg">
							<div className="card-body p-5">
								<div className="text-center mb-4">
									<div className="rounded-circle bg-success-subtle text-success display-6 d-inline-flex align-items-center justify-content-center avatar">
										<img
											src={logoSrc}
											alt="HortEra logo"
											className="gh-logo gh-logo--auth"
										/>
									</div>
									<h1 className="h3 fw-bold mt-3 mb-2 text-success-emphasis">
										{isRegisterMode
											? copy('Create Account', 'Konto erstellen', 'Kreiraj račun')
											: copy('Sign in to HortEra', 'Bei HortEra anmelden', 'Prijava u HortEra')}
									</h1>
									<p className="text-muted small mb-0">
										{isRegisterMode
											? copy(
													'Create your account to start managing your garden projects.',
													'Erstellen Sie ein Konto, um Ihre Gartenprojekte zu verwalten.',
													'Kreirajte račun kako biste upravljali svojim vrtnim projektima.'
												)
											: copy(
													'Welcome back! Sign in to continue your garden projects.',
													'Willkommen zurück! Melden Sie sich an, um Ihre Gartenprojekte fortzusetzen.',
													'Dobrodošli natrag! Prijavite se kako biste nastavili svoje vrtne projekte.'
												)}
									</p>
								</div>

								{displayError && (
									<div className="alert alert-danger alert-dismissible fade show" role="alert">
										<i className="bi bi-exclamation-triangle-fill me-2"></i>
										{displayError}
										<button
											type="button"
											className="btn-close"
											onClick={() => setError(null)}
											aria-label="Close"
										></button>
									</div>
								)}

								{gardenerInviteCodeFromUrl && (
									<div className="alert alert-success py-2 small" role="status">
										<div className="d-flex align-items-start gap-2">
											<i className="bi bi-person-plus-fill mt-1" />
											<div>
												<div className="fw-semibold">You&apos;ve been invited to join a company as a gardener</div>
												{isCheckingGardenerInvite ? (
													<div className="text-muted">Checking invite…</div>
												) : gardenerInviteInfo ? (
													<div className="text-muted">
														Company: <span className="fw-semibold">{gardenerInviteInfo.companyName}</span> · Code:{' '}
														<code className="fw-bold">{gardenerInviteInfo.code}</code>
													</div>
												) : (
													<div className="text-muted">
														This invite code is not valid (or has expired). You can still register normally.
													</div>
												)}
											</div>
										</div>
									</div>
								)}

								<form className="vstack gap-4" onSubmit={handleSubmit}>
									<div className="form-floating">
										<input
											type="email"
											className="form-control"
											id="loginEmail"
											placeholder="name@example.com"
											required
											value={email}
											onChange={(event) => setEmail(event.target.value)}
											disabled={isSubmitting || isLoading}
										/>
										<label htmlFor="loginEmail">{copy('Email', 'E-Mail', 'E-pošta')}</label>
									</div>
									<div className="form-floating">
										<input
											type="password"
											className="form-control"
											id="loginPassword"
											placeholder="••••••••"
											required
											minLength={isRegisterMode ? 8 : undefined}
											value={password}
											onChange={(event) => setPassword(event.target.value)}
											disabled={isSubmitting || isLoading}
										/>
										<label htmlFor="loginPassword">{copy('Password', 'Passwort', 'Lozinka')}</label>
										{isRegisterMode && (
											<div className="form-text">
												{copy(
													'At least 8 characters, including a letter and a number or symbol',
													'Mindestens 8 Zeichen, davon ein Buchstabe und eine Zahl oder ein Sonderzeichen',
													'Najmanje 8 znakova, uključujući slovo i broj ili simbol'
												)}
											</div>
										)}
									</div>

									{isRegisterMode && guestPlanFromUrl && (
										<div className="alert alert-success border-0 py-2 small" role="status">
											<img
												src={logoSrc}
												alt=""
												aria-hidden
												className="gh-logo gh-logo--inline me-2"
											/>
											{copy("You're signing up as a ", 'Sie registrieren sich als ', 'Registrirate se kao ')}
											<strong>{copy('garden seeker', 'Gartensuchende', 'vlasnik vrta')}</strong>
											{copy(' to unlock your garden plan.', ', um Ihren Gartenplan freizuschalten.', ' kako biste otključali plan vrta.')}
										</div>
									)}
									{isRegisterMode && seekerOnlyRegistration && !guestPlanFromUrl && (
										<div
											className="d-inline-flex align-items-center gap-2 bg-success-subtle text-success-emphasis border border-success-subtle rounded-pill px-3 py-1 small fw-semibold"
											role="status"
										>
											<img
												src={logoSrc}
												alt=""
												aria-hidden
												className="gh-logo gh-logo--inline"
											/>
											<span>{copy('You are creating a garden seeker account.', 'Sie erstellen ein Konto als Gartensuchende.', 'Kreirate račun vlasnika vrta.')}</span>
										</div>
									)}
									{isRegisterMode && (
										<>
											<div className="form-floating">
												<input
													type="text"
													className="form-control"
													id="displayName"
													placeholder="Display name"
													required
													value={displayName}
													onChange={(event) => setDisplayName(event.target.value)}
													disabled={isSubmitting || isLoading}
												/>
												<label htmlFor="displayName">{copy('Full Name', 'Vollständiger Name', 'Puno ime')}</label>
											</div>
											{!(guestPlanFromUrl && isRegisterMode) && !seekerOnlyRegistration && (
											<div>
												<label className="form-label fw-semibold text-success-emphasis">{copy('Select role', 'Rolle auswählen', 'Odaberi ulogu')}</label>
												<div className="vstack gap-3">
													{roleOptions.map((roleOption) => (
														<label
															key={roleOption.value}
															className={`role-pick card border ${
																roleOption.value === role
																	? 'border-success shadow-sm'
																	: 'border-light-subtle'
															}`}
														>
															<input
																type="radio"
																name="role"
																className="form-check-input"
																value={roleOption.value}
																checked={role === roleOption.value}
																onChange={() => {
																	if (gardenerInviteCodeFromUrl) return;
																	setRoleTouched(true);
																	setRole(roleOption.value);
																	setCompanyName('');
																	setRequestToJoinCompanyId('');
																}}
																disabled={isSubmitting || isLoading}
															/>
															<div className="card-body">
																<div className="fw-semibold text-success-emphasis">{roleOption.label}</div>
																<div className="text-muted small">{roleOption.description}</div>
															</div>
														</label>
													))}
												</div>
												{gardenerInviteCodeFromUrl && (
													<div className="form-text">
														{copy(
															'This registration link is for gardeners. Role selection is locked to ensure the invite is applied.',
															'Dieser Registrierungslink ist für Gärtner. Die Rollenwahl ist gesperrt, damit die Einladung korrekt angewendet wird.',
															'Ova registracija je za vrtlare. Odabir uloge je zaključan kako bi se pozivnica ispravno primijenila.'
														)}
													</div>
												)}
											</div>
											)}
											{role === 'gardener' && !gardenerInviteCodeFromUrl && (
												<div>
													<label className="form-label fw-semibold text-success-emphasis">
														{copy('Company (optional)', 'Unternehmen (optional)', 'Tvrtka (opcionalno)')}
													</label>
													<p className="form-text mb-2">
														{copy(
															'If you work with a landscaping company, you can request to join one now — or link your account later in Settings.',
															'Wenn Sie mit einem Gartenbauunternehmen zusammenarbeiten, können Sie jetzt einen Beitritt anfragen — oder später in den Einstellungen verknüpfen.',
															'Ako surađujete s vrtnom tvrtkom, možete zatražiti pridruživanje sada — ili povezati račun kasnije u Postavkama.'
														)}
													</p>
													<select
														className="form-select"
														value={requestToJoinCompanyId === '' ? '' : requestToJoinCompanyId}
														onChange={(e) => setRequestToJoinCompanyId(e.target.value === '' ? '' : Number(e.target.value))}
														disabled={isSubmitting || isLoading}
													>
														<option value="">
															{copy('No company yet — skip for now', 'Noch kein Unternehmen — vorerst überspringen', 'Još bez tvrtke — preskoči za sada')}
														</option>
														{companiesList.map((c) => (
															<option key={c.id} value={c.id}>{c.name}</option>
														))}
													</select>
													<p className="form-text mb-0 mt-2">
														{copy(
															'Running your own business with a team? Choose Company as your role above.',
															'Sie führen ein eigenes Unternehmen mit Team? Wählen Sie oben die Rolle Unternehmen.',
															'Vodite vlastitu tvrtku s timom? Odaberite ulogu Tvrtka gore.'
														)}
													</p>
												</div>
											)}
											{role === 'company' && (
												<div className="form-floating">
													<input
														type="text"
														className="form-control"
														id="companyName"
														placeholder="Company name"
														required={role === 'company'}
														value={companyName}
														onChange={(event) => setCompanyName(event.target.value)}
														disabled={isSubmitting || isLoading}
													/>
													<label htmlFor="companyName">{copy('Company Name', 'Firmenname', 'Naziv tvrtke')}</label>
												</div>
											)}
										</>
									)}

									<button
										type="submit"
										className="btn btn-success btn-lg w-100"
										disabled={isSubmitting || isLoading}
									>
										{isSubmitting || isLoading
											? isRegisterMode
												? copy('Creating account…', 'Konto wird erstellt…', 'Kreiranje računa…')
												: copy('Signing in…', 'Anmeldung läuft…', 'Prijava…')
											: isRegisterMode
												? copy('Create Account', 'Konto erstellen', 'Kreiraj račun')
												: copy('Sign In', 'Anmelden', 'Prijava')}
									</button>

									{!isRegisterMode && (
										<>
											<div className="d-flex align-items-center my-3">
												<hr className="flex-grow-1" />
												<span className="px-3 text-muted small">{copy('or', 'oder', 'ili')}</span>
												<hr className="flex-grow-1" />
											</div>
											<div ref={googleButtonWrapperRef} className="google-signin-wrapper">
												<div ref={googleButtonRef}></div>
											</div>
										</>
									)}
								</form>

								<div className="text-center mt-4">
									<button
										type="button"
										className="btn btn-link text-decoration-none p-0"
										onClick={() => {
											setIsRegisterMode(!isRegisterMode);
											setRoleTouched(false);
											setError(null);
											setEmail('');
											setPassword('');
											setDisplayName('');
											setCompanyName('');
											setRole('seeker');
											setRequestToJoinCompanyId('');
										}}
									>
										{isRegisterMode
											? copy('Already have an account? Sign in', 'Bereits ein Konto? Anmelden', 'Već imate račun? Prijavite se')
											: copy("Don't have an account? Create one", 'Noch kein Konto? Jetzt erstellen', 'Nemate račun? Kreirajte ga')}
									</button>
									<br />
									<Link to={seekerOnlyRegistration ? '/garden-seeker' : '/'} className="text-decoration-none small text-muted">
										<i className="bi bi-arrow-left me-1"></i>{copy('Return to landing', 'Zur Startseite', 'Povratak na početnu')}
									</Link>
								</div>
							</div>
						</div>
					</div>
					<div className="col-12 col-lg-5">
						<div className="login-side card border-0 shadow-sm">
							<div key={sidebarKey} className="card-body p-5 login-side-content">
								<p className="text-success fw-semibold text-uppercase small mb-2">{sidebarContent.eyebrow}</p>
								<h2 className="h4 text-success-emphasis mb-3">{sidebarContent.title}</h2>
								<p className="text-muted small mb-4">{sidebarContent.intro}</p>
								<ul className="list-unstyled vstack gap-3 mb-4">
									{sidebarContent.features.map((feature) => (
										<li key={feature.title} className="d-flex align-items-start gap-3">
											<i className={`bi ${feature.icon} text-success mt-1`}></i>
											<div>
												<div className="fw-semibold">{feature.title}</div>
												<div className="text-muted small">{feature.description}</div>
											</div>
										</li>
									))}
								</ul>
								<div className="alert bg-success-subtle border border-success-subtle text-success-emphasis mb-0">
									<div className="fw-semibold mb-1">{sidebarContent.calloutTitle}</div>
									<div className="text-muted small">{sidebarContent.calloutBody}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
